import React, { useContext, useEffect } from 'react';
import I18n from 'i18n-js';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCMenu } from '@reverbdotcom/cadence/components';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

import BumpRecommendationBody from '../../bump/bump_modal_body_container';
import { BumpModalShell } from '../../bump/BumpModalShell';
import { formatBid } from '../../bump/bump_helpers';
import SellerListingsCollectionContext from '../SellerListingsCollectionContext';
import SellerListingCardContext from '../SellerListingCardContext';
import { ActionType } from '../sellerListingCardReducer';
import {
  bumpRecommendationsController,
  BumpControllerProps,
  WrappedBumpComponentProps,
} from '../../bump/bump_controller';
import { trackFollowed, useTrackingContext } from '../sellerRecommendationTracking';

export const COMPONENT_NAME = 'BumpButton';

interface BumpButtonV2Props {
  listing: any;
  setShowModal: (showModal) => void;
}

export function BumpButtonV2({
  listing,
  setShowModal,
}: BumpButtonV2Props) {

  function handleClick() {
    setShowModal(true);
    trackEvent({
      eventName: MParticleEventName.ClickedComponent,
      componentName: COMPONENT_NAME,
      listingId: listing.id,
    });
  }

  return (
    <>
      <RCMenu.Item onClick={() => handleClick()}>
        {listing.bumpRate?.rate ? I18n.t('discovery.bumpButton.manageBump') : I18n.t('discovery.bumpButton.bumpItem')}
      </RCMenu.Item>
    </>
  );
}

export function BumpModalV2(props: WrappedBumpComponentProps) {
  const { dispatch } = useContext(SellerListingCardContext);
  const { useLazySellerListingsQuery } = useContext(SellerListingsCollectionContext);
  const { refetchSellerListings } = useLazySellerListingsQuery();
  const trackingContext = useTrackingContext();

  useEffect(() => {
    if (props.successMessage) {
      const message = props.bid
        ? I18n.t('discovery.bump.successListingRow', {
          percent: formatBid(props.bid),
        })
        : I18n.t('discovery.bump.successBidDelete');

      // Only when the modal was opened from a per-listing seller recommendation.
      // The same modal is reused by the action-menu "Bump" button, which does not
      // set sourceInsight and therefore does not over-count as a recommendation completion.
      if (props.sourceInsight) {
        trackFollowed(props.sourceInsight, props.listingId, trackingContext);
      }

      refetchSellerListings([props.listingId]);

      dispatch({
        type: ActionType.SHOW_TOAST,
        payload: {
          isSuccess: true,
          message,
          link: (
            <CoreLink
              to={`${window.location.pathname}${window.location.search}`}
              onClick={(e) => {
                e.preventDefault();
                window.location.reload();
              }}
            >
              {I18n.t('discovery.bump.refreshPage')}
            </CoreLink>
          ),
        },
      });
    }
  }, [props.successMessage, props.bid, props.listingId, props.sourceInsight, dispatch, trackingContext]);

  if (!props.showModal) return null;

  return (
    <BumpModalShell
      isOpen={props.showModal}
      onRequestClose={props.closeModal}
      bid={props.bid}
      showCustomWarning={props.showCustomWarning}
      onSubmit={props.onSubmit}
      onNoThankYou={props.onNoThankYou}
    >
      <BumpRecommendationBody
        hideSubmit
        bid={props.bid}
        selectedBid={props.selectedBid}
        handleBidChange={props.handleBidChange}
        listing={props.listing}
        bumpRecommendationAmount={props.bumpRecommendationAmount}
        listingLoading={props.listingLoading}
        successMessage={props.successMessage}
        failMessage={props.failMessage}
        showCustomWarning={props.showCustomWarning}
        setShowCustomWarning={props.setShowCustomWarning}
        onSubmit={props.onSubmit}
        showTopPickVariant={props.showTopPickVariant}
      />
    </BumpModalShell>
  );
}

const BumpModalV2WithController =
  bumpRecommendationsController(BumpModalV2);

export default (props: BumpControllerProps) => {
  return (
    <BumpModalV2WithController
      componentName={COMPONENT_NAME}
      listingId={props.listingId}
      bid={props.bid}
      showModal={props.showModal}
      setShowModal={props.setShowModal}
      listing={props.listing}
      sourceInsight={props.sourceInsight}
      refetch={() => { }}
    />
  );
};
